/**
 * API fetch wrapper — sends session cookie automatically via credentials: 'include'.
 * Only x-department-id is sent as a header (department switching is client-side).
 * Identity comes from the server-side session, not localStorage headers.
 *
 * A 401 response triggers a redirect to /login.
 */

/** Base URL for all API calls. Override via REACT_APP_API_URL for production. */
export const API_BASE = process.env.REACT_APP_API_URL || '';

/**
 * Build headers — only department selection, not identity.
 * @returns {Object} Headers object
 */
function getAutoHeaders() {
  const headers = {};

  const departmentId = localStorage.getItem('sfx_active_department_id');
  if (departmentId) {
    headers['x-department-id'] = departmentId;
  }

  return headers;
}

/**
 * Drop-in replacement for fetch() that sends session cookie and department header.
 * Redirects to /login on 401 responses.
 *
 * @param {string} url - The URL to fetch
 * @param {RequestInit} [options={}] - Standard fetch options
 * @returns {Promise<Response>} The fetch response
 */
export async function apiFetch(url, options = {}) {
  const autoHeaders = getAutoHeaders();

  // For methods that typically send a body, add Content-Type if not already set
  const method = (options.method || 'GET').toUpperCase();
  if (['POST', 'PUT', 'PATCH'].includes(method) && options.body && typeof options.body === 'string') {
    autoHeaders['Content-Type'] = 'application/json';
  }

  const mergedHeaders = {
    ...autoHeaders,
    ...(options.headers || {}),
  };

  const response = await fetch(url, {
    ...options,
    credentials: 'include',
    headers: mergedHeaders,
  });

  redirectToLoginOn401(response.status);

  return response;
}

// 401 = session expired or not authenticated — redirect to login. Shared by
// `apiFetch` and `apiUploadWithProgress`, so the two transports cannot disagree.
function redirectToLoginOn401(status) {
  if (status !== 401) return;
  const currentPath = window.location.pathname + window.location.search;
  // Don't redirect if already on an auth page
  if (!currentPath.startsWith('/login') && !currentPath.startsWith('/signup') &&
      !currentPath.startsWith('/forgot-password') && !currentPath.startsWith('/reset-password') &&
      !currentPath.startsWith('/set-password')) {
    window.location.href = '/login?returnUrl=' + encodeURIComponent(currentPath);
  }
}

/**
 * Upload a `FormData` body and REPORT PROGRESS — the one sanctioned transport
 * that is not `fetch` (2026-09-14). Owner: *"can we add an uploading green bar
 * … with a %"*.
 *
 * 🔑 **WHY NOT `apiFetch`:** `fetch` cannot report UPLOAD progress — there is no
 * `upload.onprogress` on a fetch, and a streamed request body is not available
 * to a multipart upload in the browsers this app supports. `XMLHttpRequest` has
 * it. Everything else `apiFetch` guarantees is reproduced here, deliberately and
 * nothing more: the session cookie (`withCredentials`), the ONE auto header
 * (`x-department-id`), the 401 → `/login?returnUrl=…` redirect, and a
 * `Response`-shaped result (`ok`, `status`, `json()`, `text()`) so callers keep
 * reading the server's `{ error, code }` exactly as they do off `apiFetch`.
 * Never add an identity header here — identity is the cookie.
 *
 * `onProgress(fraction)` receives a MONOTONIC 0…1 as bytes leave the browser.
 * A network failure REJECTS (as `fetch` does); an HTTP refusal RESOLVES with
 * `ok: false`. `signal` aborts.
 *
 * Route every order-attachment upload through `utils/orderFileMutations.js`,
 * never through this directly.
 */
export function apiUploadWithProgress(url, formData, { method = 'POST', onProgress = null, signal = null } = {}) {
  return new Promise((resolve, reject) => {
    const abortError = () => {
      const err = new Error('Upload aborted');
      err.name = 'AbortError';
      return err;
    };
    // 🪤 CHECKED BEFORE ANYTHING IS OPENED (review M2): per the XHR spec,
    // abort() before send() fires NO event, so a pre-aborted signal routed
    // through `xhr.abort()` left this promise pending for ever.
    if (signal && signal.aborted) {
      reject(abortError());
      return;
    }
    const xhr = new XMLHttpRequest();
    xhr.open(method, url);
    xhr.withCredentials = true;
    const headers = getAutoHeaders();
    Object.keys(headers).forEach((name) => xhr.setRequestHeader(name, headers[name]));

    // Strictly increasing: a repeated or backwards event is dropped, never re-sent.
    let reported = -1;
    const report = (fraction) => {
      if (typeof onProgress !== 'function' || !Number.isFinite(fraction)) return;
      const next = Math.min(1, Math.max(0, fraction));
      if (next <= reported) return;
      reported = next;
      onProgress(next);
    };
    if (xhr.upload) {
      xhr.upload.onprogress = (e) => {
        if (e && e.lengthComputable && e.total > 0) report(e.loaded / e.total);
      };
    }

    xhr.onload = () => {
      redirectToLoginOn401(xhr.status);
      if (xhr.status >= 200 && xhr.status < 300) report(1);
      const text = xhr.responseText || '';
      resolve({
        ok: xhr.status >= 200 && xhr.status < 300,
        status: xhr.status,
        text: () => Promise.resolve(text),
        json: () => new Promise((res, rej) => {
          try { res(JSON.parse(text)); } catch (err) { rej(err); }
        }),
      });
    };
    xhr.onerror = () => reject(new TypeError('Network request failed'));
    xhr.onabort = () => reject(abortError());
    if (signal) signal.addEventListener('abort', () => xhr.abort(), { once: true });
    xhr.send(formData);
  });
}

/**
 * Shorthand for GET requests.
 * @param {string} url - The URL to fetch
 * @returns {Promise<Response>}
 */
export async function apiGet(url) {
  return apiFetch(url, { method: 'GET' });
}

/**
 * Shorthand for POST requests with JSON body.
 *
 * `options` is per-call fetch options (merged AFTER method/body, so it can add
 * `headers` — e.g. `{ headers: { 'Idempotency-Key': key } }` on a protected
 * create). Per-call and OPT-IN on purpose: do NOT move idempotency keys (or any
 * dedupe) into `apiFetch`/`getAutoHeaders` globally — legitimately-repeated
 * POSTs (poke, take/release) must keep their semantics.
 *
 * @param {string} url - The URL to post to
 * @param {Object} body - The request body (will be JSON-stringified)
 * @param {RequestInit} [options={}] - Extra fetch options (e.g. headers)
 * @returns {Promise<Response>}
 */
export async function apiPost(url, body, options = {}) {
  return apiFetch(url, {
    method: 'POST',
    body: JSON.stringify(body),
    ...options,
  });
}

/**
 * Shorthand for PUT requests with JSON body.
 * @param {string} url - The URL to put to
 * @param {Object} body - The request body (will be JSON-stringified)
 * @returns {Promise<Response>}
 */
export async function apiPut(url, body) {
  return apiFetch(url, {
    method: 'PUT',
    body: JSON.stringify(body),
  });
}

/**
 * Shorthand for PATCH requests with JSON body.
 * @param {string} url - The URL to patch
 * @param {Object} body - The request body (will be JSON-stringified)
 * @returns {Promise<Response>}
 */
export async function apiPatch(url, body) {
  return apiFetch(url, {
    method: 'PATCH',
    body: JSON.stringify(body),
  });
}

/**
 * Shorthand for DELETE requests.
 *
 * `body` is OPTIONAL (2026-09-23, for `DELETE /api/teams/:teamId`'s
 * `{ gagOwnership }`). Omitted → the request is exactly the one-argument
 * request it always was: no body, no Content-Type. Given → JSON with an
 * explicit `Content-Type`, because `apiFetch` adds that header only for
 * POST/PUT/PATCH and express.json() ignores a body without it.
 * Pinned by `api.deleteBody.test.js`.
 * @param {string} url - The URL to delete
 * @param {Object} [body] - Optional request body (JSON-stringified)
 * @returns {Promise<Response>}
 */
export async function apiDelete(url, body) {
  if (body === undefined) return apiFetch(url, { method: 'DELETE' });
  return apiFetch(url, {
    method: 'DELETE',
    body: JSON.stringify(body),
    headers: { 'Content-Type': 'application/json' },
  });
}

/**
 * Build a richer Error from a non-ok API response so callers don't lose
 * the server's structured `code` field (e.g. `code: 'version_locked'`
 * from HTTP 423 lock-guard rejections). The body is read at most once;
 * non-JSON responses are tolerated. The thrown Error carries `.code`,
 * `.status`, and `.body` so `friendlyErrorMessage` can map structured
 * codes to tailored toast copy instead of substring-sniffing the message.
 *
 * Usage:
 *   const res = await apiPut(url, body);
 *   if (!res.ok) await apiThrow(res);
 *   const data = await res.json();
 *
 * If the caller has already consumed the body (e.g. `const data = await
 * res.json()` before the ok check), construct the Error inline instead:
 *   const err = new Error(data.error || 'Failed...');
 *   if (data.code) err.code = data.code;
 *   throw err;
 */
export async function apiThrow(res) {
  let data = {};
  try { data = await res.json(); } catch { /* tolerate non-JSON */ }
  const err = new Error(data.error || `Request failed (HTTP ${res.status})`);
  if (data.code) err.code = data.code;
  err.status = res.status;
  err.body = data;
  throw err;
}
